import React from 'react';
import { useDarkClasses } from '../../../../hooks/useDarkClasses';
import { HORROR_MODULE_DATA } from '../genreModuleData';
import { SearchableChipField, SingleSelectField } from './shared';
const ACCENT = 'bg-red-100 text-red-700 border-red-500 dark:bg-red-900/30 dark:text-red-300';
// ═══════════════════════════════════════════════════
// FULL HORROR MODULE
// ═══════════════════════════════════════════════════
interface HorrorModuleProps {
  subtype: string;
  setSubtype: (v: string) => void;
  fearMechanisms: string[];
  setFearMechanisms: React.Dispatch<React.SetStateAction<string[]>>;
  storyEngines: string[];
  setStoryEngines: React.Dispatch<React.SetStateAction<string[]>>;
  intensity: string;
  setIntensity: (v: string) => void;
  extremeLayers: string[];
  setExtremeLayers: React.Dispatch<React.SetStateAction<string[]>>;
}
export const HorrorModule: React.FC<HorrorModuleProps> = ({
  subtype,
  setSubtype,
  fearMechanisms,
  setFearMechanisms,
  storyEngines,
  setStoryEngines,
  intensity,
  setIntensity,
  extremeLayers,
  setExtremeLayers
}) => {
  const dk = useDarkClasses();
  const toggleFear = (f: string) => setFearMechanisms(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  const toggleEngine = (e: string) => setStoryEngines(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  const toggleExtreme = (l: string) => setExtremeLayers(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);
  const showExtreme = intensity === 'Brutal' || intensity === 'Extreme';
  return <div className="space-y-8" data-id="element-20568" data-mp="mncvczcj2tgr">
      {/* Horror Subtype */}
      <SingleSelectField label="Horror Subtype" placeholder="e.g. Slasher, Paranormal, Body Horror..." items={HORROR_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-20569" data-mp="rnhbupdfro6z" />

      {/* Fear Mechanism */}
      <SearchableChipField label="Fear Mechanism" placeholder="e.g. Isolation, Unknown, Gore..." items={HORROR_MODULE_DATA.fearMechanisms} selected={fearMechanisms} onToggle={toggleFear} accentClass={ACCENT} data-id="element-20570" data-mp="1k5uyrtx8hpra" />

      {/* Horror Story Engine */}
      <SearchableChipField label="Horror Story Engine" placeholder="e.g. Survival, Investigation, Escape..." items={HORROR_MODULE_DATA.storyEngines} selected={storyEngines} onToggle={toggleEngine} accentClass={ACCENT} data-id="element-20571" data-mp="1l16qtg6uqvj0" />

      {/* Horror Intensity */}
      <div data-id="element-20572" data-mp="168d1l31s3115">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-20573" data-mp="11atpa8on4ybu">
          Horror Intensity
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-20574" data-mp="xaavi936ti70">
          {HORROR_MODULE_DATA.intensities.map(hi => {
          const isSelected = intensity === hi.level;
          return <button key={hi.level} onClick={() => setIntensity(isSelected ? '' : hi.level)} className={`p-3 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-red-700 text-white border-red-700 dark:bg-red-400 dark:text-red-950' : `${dk.surfaceMuted} ${dk.border} hover:border-red-400`}`} data-id="element-20575" data-mp="ncmyfci3zylo">
                <div className={`font-bold text-sm mb-1 ${isSelected ? '' : dk.textPrimary}`} data-id="element-20576" data-mp="3cyq6vz9tleb8">
                  {hi.level}
                </div>
                <div className={`text-xs ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-20577" data-mp="2l70rk9vu36bm">
                  {hi.description}
                </div>
              </button>;
        })}
        </div>
      </div>

      {/* Extreme Horror Layer — only when Brutal or Extreme */}
      {showExtreme && <div className="p-5 rounded-xl border-2 border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10" data-id="element-20578" data-mp="1xh6je5mgm5xp">
          <div className="mb-4" data-id="element-20579" data-mp="21h6w9ltwu92n">
            <label className={`block text-sm font-bold mb-1 text-red-700 dark:text-red-400`} data-id="element-20580" data-mp="1ieetb0erwcb5">
              ⚠️ Extreme Horror Layer
            </label>
            <p className={`text-xs ${dk.textMuted}`} data-id="element-20581" data-mp="1ddqu3rge4p5u">
              Select any applicable extreme content classifications. These will
              be visible to readers as content warnings.
            </p>
          </div>

          <SearchableChipField label="Horror-Specific Warnings" placeholder="Search extreme content warnings..." items={HORROR_MODULE_DATA.extremeHorrorLayers} selected={extremeLayers} onToggle={toggleExtreme} accentClass="bg-red-600 text-white border-red-600 dark:bg-red-500 dark:border-red-500" data-id="element-20582" data-mp="214wbib1zbpi9" />
        </div>}
    </div>;
};
// ═══════════════════════════════════════════════════
// COMPACT CROSSOVER
// ═══════════════════════════════════════════════════
interface HorrorModuleCompactProps {
  subtype: string;
  setSubtype: (v: string) => void;
  fearMechanisms: string[];
  setFearMechanisms: React.Dispatch<React.SetStateAction<string[]>>;
  intensity: string;
  setIntensity: (v: string) => void;
}
export const HorrorModuleCompact: React.FC<HorrorModuleCompactProps> = ({
  subtype,
  setSubtype,
  fearMechanisms,
  setFearMechanisms,
  intensity,
  setIntensity
}) => {
  const dk = useDarkClasses();
  const toggleFear = (f: string) => setFearMechanisms(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  return <div className="space-y-5" data-id="element-20583" data-mp="28q8eszere1hb">
      <SingleSelectField label="Horror Subtype" placeholder="Search subtypes..." items={HORROR_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-20584" data-mp="1uow2cxyt4i06" />

      <SearchableChipField label="Fear Mechanism" placeholder="Search fears..." items={HORROR_MODULE_DATA.fearMechanisms} selected={fearMechanisms} onToggle={toggleFear} accentClass={ACCENT} data-id="element-20585" data-mp="3p2nkymz46c7r" />

      <div data-id="element-20586" data-mp="3sirtei36uhaa">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-20587" data-mp="3sii46wpywe5n">
          Horror Intensity
        </label>
        <div className="grid grid-cols-2 gap-2" data-id="element-20588" data-mp="3im5a3xqifyad">
          {HORROR_MODULE_DATA.intensities.map(hi => {
          const isSelected = intensity === hi.level;
          return <button key={hi.level} onClick={() => setIntensity(isSelected ? '' : hi.level)} className={`p-2.5 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-red-700 text-white border-red-700 dark:bg-red-400 dark:text-red-950' : `${dk.surfaceMuted} ${dk.border}`}`} data-id="element-20589" data-mp="f5opsu8xx68k">
                <div className={`font-bold text-xs mb-0.5 ${isSelected ? '' : dk.textPrimary}`} data-id="element-20590" data-mp="gsacc4k0qxx">
                  {hi.level}
                </div>
                <div className={`text-[10px] ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-20591" data-mp="t4698frtbwko">
                  {hi.description}
                </div>
              </button>;
        })}
        </div>
      </div>
    </div>;
};